/**
 * Collapses concurrent calls of an async operation into one in-flight promise.
 *
 * Renewal is the motivating case: a page that fires twenty requests on load and
 * gets twenty 401s must send **one** renewal, not twenty. The existing codebase
 * solves this with a module-level mutable singleton in the axios helper, which
 * works but is global and untestable; this is the same idea scoped to an owner.
 */
export function singleFlight<T>(operation: () => Promise<T>): () => Promise<T> {
  let inFlight: Promise<T> | null = null;

  return () => {
    if (inFlight) return inFlight;

    inFlight = operation().finally(() => {
      inFlight = null;
    });

    return inFlight;
  };
}

/**
 * Rejects with `timeoutError` if `promise` has not settled within `ms`.
 *
 * Used wherever we depend on something outside our control answering — an
 * embedding host, or the identity provider's session probe. CORE-2655 §4.3
 * requires those to be time-boxed rather than able to hang a boot.
 */
export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  timeoutError: () => Error,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(timeoutError()), ms);

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}
